import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  setPayingWithSourceId,
  setPayingForResidentId,
  setIsPaymentModalOpen,
} from '@/store/slices/residents/paymentsSlice';
import { useLeaseAutoPay } from '@/features/payments/hooks/useLeaseAutoPay';
import { formatCurrency } from '@/utils/common/formatting';
import {
  getPaymentMethodLabel,
  generatePaymentSummaryRows
} from '@/utils/residents/paymentUtils';

type Params = {
  leaseId: number;
  residentId?: number;
  amount?: number;
  paymentMethod?: any;
  calculatedFees?: any;
  onBehalfOfName?: string;
  // Optional processor reference fields — shown if present so support can
  // correlate the charge back to CardConnect/Stitch and the Resio DB row.
  paymentId?: number;
  retref?: string;
  authcode?: string;
};

export const TransactionReceivedScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const DESKTOP_COLORS = useDesktopColors();
  const { colors } = useThemeColors();

  const {
    leaseId,
    residentId,
    amount,
    paymentMethod,
    calculatedFees,
    onBehalfOfName,
    paymentId,
    retref,
    authcode,
  } = (route.params || {}) as Params;

  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userType = useAppSelector((s) => (s as any).auth?.user?.type);

  // Determine if this is owner payment
  const isOwner = useMemo(() => {
    return !residentId || residentId === authUser?.id;
  }, [residentId, authUser]);

  // Payment summary rows
  const summaryRows = useMemo(() => {
    if (!calculatedFees) return [];

    const displayName = isOwner
      ? `${authUser?.firstName || ''} ${authUser?.lastName || ''}`.trim()
      : onBehalfOfName || 'Resident';

    return generatePaymentSummaryRows(
      isOwner,
      displayName,
      paymentMethod,
      amount || 0,
      calculatedFees.convenienceFee || 0,
      calculatedFees.total || amount || 0
    );
  }, [isOwner, authUser, onBehalfOfName, paymentMethod, amount, calculatedFees]);

  const { hasAutoPay: hasAutoPaySetUp, isLoading: isLoadingLease } = useLeaseAutoPay(leaseId);

  const resetPaymentState = () => {
    dispatch(setPayingWithSourceId(0));
    dispatch(setPayingForResidentId(0));
    dispatch(setIsPaymentModalOpen(false));
  };

  const handleClose = () => {
    resetPaymentState();
    // Navigate back to payments screen
    navigation.replace('Payments');
  };

  const handleSetupAutoPay = () => {
    resetPaymentState();
    // Navigate to AutoPay wizard modal - matching web app flow
    navigation.replace('AutoPayWizard', {
      leaseId,
      residentId,
      fromTransactionReceived: true
    });
  };

  const getConfirmationMessage = () => {
    if (userType === 'guest' && !isOwner) {
      const residentName = onBehalfOfName || 'the resident';
      return `Your transaction has been received. A confirmation has been sent to your email and to ${residentName}.`;
    }

    return `Your transaction has been received. A confirmation has been sent to ${authUser?.email || 'your email'}.`;
  };

  const footerElement = !hasAutoPaySetUp && !isLoadingLease ? (
    <View style={styles.footerRow}>
      <ModalButton label="No, thanks, I'm finished" variant="secondary" onPress={handleClose} />
      <ModalButton label="Set up a recurring payment" variant="primary" onPress={handleSetupAutoPay} />
    </View>
  ) : hasAutoPaySetUp ? (
    <View style={styles.footerRow}>
      <ModalButton label="Close" variant="primary" onPress={handleClose} />
    </View>
  ) : undefined;

  return (
    <UnifiedModal
      visible={true}
      variant="dialog"
      dialogSize="large"
      onClose={handleClose}
      title="Thank you!"
      scrollable
      footer={footerElement}
    >
      <View style={styles.successHeader}>
        <SuccessIcon />
        <Text style={[styles.title, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Thank you!</Text>
      </View>

      <Text style={[styles.message, { color: DESKTOP_COLORS.TEXT_SECONDARY }]}>
        {getConfirmationMessage()}
      </Text>

      {summaryRows.length > 0 && (
        <View style={styles.summarySection}>
          <Text style={[styles.summaryTitle, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Payment Summary</Text>
          <View style={[styles.summaryCard, { backgroundColor: colors.activityIcon.bg }]}>
            {summaryRows.map(([label, value], index) => (
              <View key={index} style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>{label}</Text>
                <Text style={[styles.summaryValue, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>{value}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {(retref || authcode || paymentId != null) && (
        <View style={styles.summarySection}>
          <Text style={[styles.summaryTitle, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Reference</Text>
          <View style={[styles.summaryCard, { backgroundColor: colors.activityIcon.bg }]}>
            {retref && (
              <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>Reference #</Text>
                <Text style={[styles.summaryValue, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>{retref}</Text>
              </View>
            )}
            {authcode && (
              <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>Authorization</Text>
                <Text style={[styles.summaryValue, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>{authcode}</Text>
              </View>
            )}
            {paymentId != null && (
              <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>Payment ID</Text>
                <Text style={[styles.summaryValue, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>{paymentId}</Text>
              </View>
            )}
          </View>
        </View>
      )}

      <View style={[styles.processingNote, { backgroundColor: colors.activityIcon.bg }]}>
        <InfoIcon />
        <Text style={[styles.processingText, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>
          Payments can take up to 5 business days to process completely.
          Please be sure to keep sufficient funds in your account.
        </Text>
      </View>

      {!hasAutoPaySetUp && !isLoadingLease && (
        <View style={[styles.autoPaySection, { backgroundColor: colors.activityIcon.bg }]}>
          <Text style={[styles.autoPayTitle, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>
            Save time by setting up a recurring payment?
          </Text>
        </View>
      )}
    </UnifiedModal>
  );
};

// Helper Icons
const SuccessIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHECK_CIRCLE;
  return Icon ? (
    <Icon width={64} height={64} color="#10B981" />
  ) : (
    <View style={styles.successIconFallback}>
      <Text style={styles.successIconText}>✓</Text>
    </View>
  );
};

const InfoIcon: React.FC = () => {
  const DESKTOP_COLORS = useDesktopColors();
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).INFORMATION_CIRCLE;
  return Icon ? (
    <Icon width={20} height={20} color={DESKTOP_COLORS.TEXT_TERTIARY} />
  ) : null;
};

const styles = StyleSheet.create({
  successHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  successIconFallback: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  successIconText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginTop: 16,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
  },
  summarySection: {
    marginBottom: 32,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  summaryCard: {
    borderRadius: 12,
    padding: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  summaryLabel: {
    fontSize: 14,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  processingNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderRadius: 12,
    padding: 16,
    marginBottom: 32,
  },
  processingText: {
    fontSize: 12,
    lineHeight: 18,
    marginLeft: 12,
    flex: 1,
  },
  autoPaySection: {
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    marginBottom: 24,
  },
  autoPayTitle: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 20,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 12,
  },
});

export default TransactionReceivedScreen;
